import type { TRequirements } from '../types/global/helper';

/**
 * Converts a contest requirement item (plain string or structured) to text for display / link parsing.
 */
export const requirementItemToPlainText = (item: string | TRequirements): string => {
    if (typeof item === 'string') return item;
    const header = item.header?.trim() ?? '';
    const detail = item.detail?.trim() ?? '';
    if (header && detail) return `${header}\n${detail}`;
    return header || detail;
};

/**
 * Normalizes API requirements (strings or structured objects) to a string array for editors and lists.
 */
export const requirementsListToStringArray = (
    items: string[] | TRequirements[] | undefined | null,
): string[] => {
    if (!items?.length) return [];
    return items.map(requirementItemToPlainText);
};

/**
 * Generates a random string of specified length
 * @param length - The length of the random string to generate
 * @returns A random string containing alphanumeric characters
 */
export const generateRandomString = (length: number): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

/**
 * Generates a random string containing only uppercase letters and numbers
 * @param length - The length of the random string to generate
 * @returns A random string containing only uppercase letters and numbers
 */
export const generateRandomCode = (length: number): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

/**
 * Parses custom link syntax and converts it to HTML anchor tags
 * Syntax: <link text="Link Text">url<> or <link text="Link Text">url</link>
 * @param text - The text containing custom link syntax
 * @returns The text with custom links converted to HTML anchor tags
 */
export const parseCustomLinks = (text: string): string => {
    if (!text) return text;

    // Decode HTML entities first (in case the text was escaped)
    // Handle both browser and Node.js environments
    let decodedText = text;
    if (typeof document !== 'undefined') {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        decodedText = textarea.value;
    } else {
        // Fallback for Node.js/SSR - basic entity decoding
        decodedText = text
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, '&');
    }

    // Match pattern: <link text="...">url<> or <link text="...">url</link>
    // Handles both closing tag formats: <> and </link>
    // Case insensitive and handles whitespace variations
    const linkPattern = /<link\s+text\s*=\s*["']([^"']+)["']\s*>([^<]+)(?:<>|<\/link>)/gi;

    return decodedText.replace(linkPattern, (match, linkText, url) => {
        // Clean up the captured values
        const cleanLinkText = linkText.trim();
        let href = url.trim();

        // Remove any trailing whitespace or closing tag remnants
        href = href.replace(/\s*$/, '').replace(/<\/link>$/, '');

        // Ensure URL has protocol
        if (!href.match(/^https?:\/\//i)) {
            href = `https://${href}`;
        }
        // Create anchor tag with proper attributes for security
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="custom-parsed-link">${cleanLinkText}</a>`;
    });
};
